/**
 * app/screens/LoginScreen.tsx
 * ----------------------------
 * What: Sign-in screen for existing users.
 * Why: Entry point to authenticated app flows.
 */
import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity, Text, StyleSheet, SafeAreaView, StatusBar, Platform, Modal } from 'react-native';
import { useAuth } from '../../lib/AuthContext';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import CustomAlert from '../components/CustomAlert';
import ThemedButton from '../components/ThemedButton';
import { Ionicons } from '@expo/vector-icons';
import * as LocalAuthentication from 'expo-local-authentication';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '../../lib/supabase';
import { useTheme } from '../../lib/ThemeContext';
import FormTextInput from '../components/FormTextInput';

type RootStackParamList = {
  Login: undefined;
  SignUp: undefined;
  Home: undefined;
};

type Props = NativeStackScreenProps<RootStackParamList, 'Login'>;

export default function LoginScreen({ navigation }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ visible: false, title: '', message: '' });
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showResetSentModal, setShowResetSentModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState('');
  const [showUnverifiedModal, setShowUnverifiedModal] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const { signIn } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    checkBiometricAvailability();
    // Load remembered email
    (async () => {
      try {
        const stored = await AsyncStorage.getItem('rememberedEmail');
        if (stored) {
          setEmail(stored);
          setRememberMe(true);
        }
      } catch {}
    })();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      const compatible = await LocalAuthentication.hasHardwareAsync();
      const enrolled = await LocalAuthentication.isEnrolledAsync();
      setIsBiometricAvailable(compatible && enrolled);
    } catch (error) {
      console.error('Error checking biometric availability:', error);
    }
  };

  const handleBiometricAuth = async () => {
    try {
      const result = await LocalAuthentication.authenticateAsync({
        promptMessage: 'Authenticate to login',
        fallbackLabel: 'Use password',
      });

      if (result.success) {
        // Retrieve stored credentials
        const storedEmail = await AsyncStorage.getItem('userEmail');
        const storedPassword = await AsyncStorage.getItem('userPassword');

        if (storedEmail && storedPassword) {
          setIsLoading(true);
          try {
            const { data, error } = await signIn(storedEmail, storedPassword);
            if (error) throw error;
            if (!data?.user) throw new Error('Login failed');
          } catch (error) {
            setAlert({
              visible: true,
              title: 'Authentication Failed',
              message: 'Biometric authentication succeeded but login failed. Please try using your password.',
            });
          } finally {
            setIsLoading(false);
          }
        } else {
          setAlert({
            visible: true,
            title: 'No Stored Credentials',
            message: 'Please login with your password first to enable biometric login.',
          });
        }
      }
    } catch (error) {
      console.error('Error during biometric authentication:', error);
      setAlert({
        visible: true,
        title: 'Authentication Error',
        message: 'Failed to authenticate. Please try using your password.',
      });
    }
  };

  const handleResendVerification = async () => {
    if (!email) return;
    
    setIsResendingVerification(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });

      if (error) throw error;

      setShowUnverifiedModal(false);
      setShowInfoModal(true);
      setInfoModalMessage('Verification email has been resent. Please check your inbox.');
    } catch (error: any) {
      setAlert({
        visible: true,
        title: 'Error',
        message: error.message || 'Failed to resend verification email.',
      });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password.trim()) {
      setInfoModalMessage('Please enter both email and password.');
      setShowInfoModal(true);
      return;
    }

    setIsLoading(true);

    // Persist or clear remembered email based on checkbox
    try {
      if (rememberMe) {
        await AsyncStorage.setItem('rememberedEmail', email);
      } else {
        await AsyncStorage.removeItem('rememberedEmail');
      }
    } catch {}

    try {
      const { data, error } = await signIn(email, password);
      
      if (error) {
        console.error('Login error:', error);
        let errorMessage = 'Invalid email or password. Please try again.';
        
        if (error.message.includes('Invalid login credentials')) {
          setErrorModalMessage('Invalid email or password. Please check your credentials and try again.');
          setShowErrorModal(true);
          setIsLoading(false);
          return;
        } else if (error.message.includes('Email not confirmed')) {
          setShowUnverifiedModal(true);
          setIsLoading(false);
          return;
        } else if (error.message.includes('rate limit')) {
          errorMessage = 'Too many login attempts. Please try again later.';
        }
        
        throw new Error(errorMessage);
      }

      if (!data?.user) {
        throw new Error('Login failed. Please try again.');
      }

      // Store credentials for biometric login
      await AsyncStorage.setItem('userEmail', email);
      await AsyncStorage.setItem('userPassword', password);
    } catch (error: any) {
      if (!error.message.includes('Email not confirmed')) {
        setErrorModalMessage(error.message || 'An error occurred during login. Please try again.');
        setShowErrorModal(true);
      }
    } finally {
      if (!showUnverifiedModal && !showErrorModal) {
        setIsLoading(false);
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail.trim()) {
      setAlert({
        visible: true,
        title: 'Email Required',
        message: 'Please enter your email address.',
      });
      return;
    }

    setIsSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail, {
        redirectTo: window.location.origin,
      });

      if (error) throw error;

      setShowForgotPasswordModal(false);
      setForgotPasswordEmail('');
      setShowResetSentModal(true);
    } catch (error: any) {
      setAlert({
        visible: true,
        title: 'Error',
        message: error.message || 'An error occurred while sending the reset email.',
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.background }]}>
      <StatusBar barStyle="light-content" backgroundColor={theme.background} />
      <View style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={[styles.header, { borderBottomColor: theme.border }]}>
          <Text style={[styles.title, { color: theme.text }]}>Log My Home</Text>
        </View>

        <View style={[styles.content]}>
          <Text style={[styles.welcomeText, { color: theme.text }]}>Welcome Back</Text>
          <Text style={[styles.subtitle, { color: theme.text }]}>Sign in to continue</Text>

          <View style={[styles.form]}>
            <FormTextInput
              style={[styles.input, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
              placeholder="Email"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              keyboardType="email-address"
              accessibilityLabel="Email input"
              accessibilityHint="Enter your email address"
              placeholderTextColor={theme.secondaryText}
            />
            <View style={[styles.passwordContainer, { borderColor: theme.border }]}>
              <FormTextInput
                style={[styles.input, styles.passwordInput, { backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                onSubmitEditing={handleSignIn}
                returnKeyType="done"
                accessibilityLabel="Password input"
                accessibilityHint="Enter your password"
                placeholderTextColor={theme.secondaryText}
              />
              <TouchableOpacity
                style={[styles.eyeButton, { backgroundColor: theme.inputBackground }]}
                onPress={() => setShowPassword(!showPassword)}
                accessibilityLabel={showPassword ? "Hide password" : "Show password"}
              >
                <Ionicons
                  name={showPassword ? "eye-off" : "eye"}
                  size={24}
                  color={theme.text}
                />
              </TouchableOpacity>
            </View>

            <View style={[styles.rememberMeContainer, { borderColor: theme.border }]}>
              <TouchableOpacity
                style={[styles.rememberMeCheckbox, { backgroundColor: theme.inputBackground }]}
                onPress={() => setRememberMe(!rememberMe)}
                accessibilityLabel={rememberMe ? "Remember me off" : "Remember me on"}
              >
                {rememberMe && <Ionicons name="checkmark" size={20} color={theme.accent} />}
              </TouchableOpacity>
              <Text style={[styles.rememberMeText, { color: theme.text }]}>Remember me</Text>
            </View>

            <TouchableOpacity
              style={styles.forgotPasswordButton}
              onPress={() => setShowForgotPasswordModal(true)}
            >
              <Text style={[styles.forgotPasswordText, { color: theme.text }]}>Forgot Password?</Text>
            </TouchableOpacity>

            <ThemedButton
              title={isLoading ? 'Signing In...' : 'Sign In'}
              onPress={handleSignIn}
              disabled={isLoading}
              accessibilityLabel={isLoading ? 'Signing in' : 'Sign in'}
            />

            {isBiometricAvailable && (
              <TouchableOpacity
                style={[styles.biometricButton, { backgroundColor: theme.card }]}
                onPress={handleBiometricAuth}
                accessibilityRole="button"
                accessibilityLabel="Sign in with biometrics"
              >
                <Ionicons
                  name={Platform.OS === 'ios' ? 'finger-print' : 'finger-print-outline'}
                  size={24}
                  color={theme.accent}
                />
                <Text style={[styles.biometricButtonText, { color: theme.text }]}>
                  Sign in with {Platform.OS === 'ios' ? 'Face ID' : 'Biometrics'}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity 
              style={styles.linkButton}
              onPress={() => navigation.navigate('SignUp')}
              accessibilityRole="link"
              accessibilityLabel="Don't have an account? Sign up"
            >
              <Text style={[styles.linkText, { color: theme.text }]}>Don't have an account? Sign up</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Forgot Password Modal */}
        <Modal
          visible={showForgotPasswordModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowForgotPasswordModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Reset Password</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>
                Enter your email address and we'll send you instructions to reset your password.
              </Text>
              <FormTextInput
                style={[styles.input, { marginBottom: 20, backgroundColor: theme.inputBackground, borderColor: theme.border, color: theme.text }]}
                placeholder="Enter your email"
                value={forgotPasswordEmail}
                onChangeText={setForgotPasswordEmail}
                autoCapitalize="none"
                keyboardType="email-address"
                placeholderTextColor={theme.secondaryText}
              />
              <View style={[styles.modalButtons, { backgroundColor: theme.card }]}>
                <ThemedButton
                  title="Cancel"
                  variant="secondary"
                  onPress={() => setShowForgotPasswordModal(false)}
                  style={{ height: 44, flex:1 }}
                />
                <ThemedButton
                  title={isSendingReset ? 'Sending...' : 'Confirm'}
                  onPress={handleForgotPassword}
                  disabled={isSendingReset}
                  style={{ height: 44, flex:1 }}
                />
              </View>
            </View>
          </View>
        </Modal>

        {/* Reset Sent Confirmation Modal */}
        <Modal
          visible={showResetSentModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowResetSentModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Password Reset Email Sent</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>
                If an account exists with this email, you will receive password reset instructions shortly.
              </Text>
              <ThemedButton title="OK" onPress={() => setShowResetSentModal(false)} />
            </View>
          </View>
        </Modal>

        {/* Info/Success Modal */}
        <Modal
          visible={showInfoModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowInfoModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>
                {infoModalMessage.includes('Verification email') ? 'Success' : 'Error'}
              </Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>{infoModalMessage}</Text>
              <ThemedButton title="OK" onPress={() => setShowInfoModal(false)} style={{ minWidth: 140, alignSelf: 'center' }} />
            </View>
          </View>
        </Modal>

        {/* Unverified Email Modal */}
        <Modal
          visible={showUnverifiedModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowUnverifiedModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Email Not Verified</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>
                Please verify your email address before logging in. If you haven't received the verification email, please check your spam folder or request a new verification link.
              </Text>
              <View style={[styles.modalButtons, { backgroundColor: theme.card }]}>
                <ThemedButton title="Close" variant="secondary" onPress={() => setShowUnverifiedModal(false)} style={{flex:1}} />
                <ThemedButton
                  title={isResendingVerification ? 'Sending...' : 'Resend Verification'}
                  onPress={handleResendVerification}
                  disabled={isResendingVerification}
                  style={{flex:1}}
                />
              </View>
            </View>
          </View>
        </Modal>

        {/* Error Modal */}
        <Modal
          visible={showErrorModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowErrorModal(false)}
        >
          <View style={[styles.modalOverlay, { backgroundColor: theme.background }]}>
            <View style={[styles.modalContent, { backgroundColor: theme.card }]}>
              <Text style={[styles.modalTitle, { color: theme.text }]}>Login Failed</Text>
              <Text style={[styles.modalMessage, { color: theme.text }]}>{errorModalMessage}</Text>
              <ThemedButton
                title="OK"
                onPress={() => setShowErrorModal(false)}
                style={{ minWidth: 140, alignSelf: 'center' }}
              />
            </View>
          </View>
        </Modal>

        <CustomAlert
          visible={alert.visible}
          title={alert.title}
          message={alert.message}
          onDismiss={() => setAlert(prev => ({ ...prev, visible: false }))}
        />
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 15,
    borderBottomWidth: 1,
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    marginBottom: 30,
    textAlign: 'center',
  },
  form: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  input: {
    borderWidth: 1,
    padding: 15,
    borderRadius: 6,
    marginBottom: 15,
    fontSize: 16,
  },
  passwordContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
  },
  passwordInput: {
    flex: 1,
    marginBottom: 0,
  },
  eyeButton: {
    position: 'absolute',
    right: 15,
    padding: 5,
  },
  button: {
    padding: 15,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 10,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  biometricButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    marginTop: 15,
    gap: 8,
  },
  biometricButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 16,
  },
  forgotPasswordButton: {
    alignSelf: 'flex-end',
    marginTop: -10,
    marginBottom: 10,
  },
  forgotPasswordText: {
    color: '#007bff',
    fontSize: 14,
  },
  rememberMeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 15,
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 6,
  },
  rememberMeCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#555',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  rememberMeText: {
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#2a2a2a',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '500',
    marginBottom: 12,
    textAlign: 'center',
  },
  modalMessage: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
  },
  modalButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 120,
  },
  cancelButton: {
    backgroundColor: '#333',
  },
  sendButton: {
    backgroundColor: '#007bff',
  },
  modalButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '500',
  },
}); 